import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarCheck } from 'lucide-react';
import WidgetShell from '@/components/admin/dashboard/WidgetShell';
import api from '@/lib/api';
import { formatDateShort } from '@/lib/utils';

/**
 * Upcoming arrivals — the next few confirmed check-ins. Mirrors the fields the
 * `/admin/reservations` list handler returns (reservations.ts).
 */
interface Reservation {
  id: string;
  code: string;
  guest_name: string;
  check_in: string;
  check_out: string;
  status: string;
  property_title: string;
}

interface Resp {
  reservations: Reservation[];
}

function ymd(d: Date): string {
  return d.toISOString().slice(0, 10);
}

export default function UpcomingReservationsWidget() {
  const today = ymd(new Date());

  const { data, isLoading, error } = useQuery({
    queryKey: ['dashboard', 'reservations', 'upcoming', today],
    // order=asc so the server returns the SOONEST check-ins first (not truncated
    // past LIMIT when a tenant has many future stays); limit=5 keeps it light.
    queryFn: () =>
      api.get<Resp>(`/admin/reservations?status=confirmed&since=${today}&order=asc&limit=5`),
    staleTime: 60000,
  });

  const upcoming = (data?.reservations ?? [])
    .slice()
    .sort((a, b) => a.check_in.localeCompare(b.check_in))
    .slice(0, 5);

  return (
    <WidgetShell
      title="Upcoming arrivals"
      icon={<CalendarCheck />}
      to="/admin/reservations"
      isLoading={isLoading}
      error={error}
      isEmpty={!!data && upcoming.length === 0}
      emptyIcon={<CalendarCheck />}
      emptyMessage="No upcoming arrivals."
    >
      {upcoming.length > 0 && (
        <ul className="divide-y divide-line">
          {upcoming.map((r) => (
            <li key={r.id}>
              <Link
                to={`/admin/reservations/${r.id}`}
                className="flex items-center justify-between gap-3 py-2.5 transition-colors hover:bg-stone-50"
              >
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-ink">{r.guest_name}</p>
                  <p className="truncate text-xs text-stone-500">{r.property_title}</p>
                </div>
                <span className="shrink-0 text-xs font-medium text-stone-500">
                  {formatDateShort(r.check_in)}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </WidgetShell>
  );
}
